import React from "react";
import { BookOpen, Download, Zap, ArrowRight } from "lucide-react";
import CodeBlock from "../components/CodeBlock";
import { Card } from "../components/ui/card";

const GettingStarted = () => {
  return (
    <div className="prose prose-invert max-w-none">
      {/* Header */}
      <div className="not-prose mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
          <BookOpen className="h-4 w-4" />
          Getting Started
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Introduction to AxisNet
        </h1>
        <p className="text-xl text-slate-400">
          AxisNet is a comprehensive C# framework that helps you build modular,
          enterprise-grade applications with clean architecture principles.
        </p>
      </div>

      {/* What is AxisNet */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-white mb-4">What is AxisNet?</h2>
        <p className="text-slate-300 mb-6">
          AxisNet provides three powerful tools that work together to accelerate
          your .NET development:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 not-prose mb-8">
          <Card className="p-6 bg-slate-900/50 border-slate-800">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 mb-4">
              <span className="text-2xl font-bold">1</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              C# Library
            </h3>
            <p className="text-sm text-slate-400">
              Foundation classes, interfaces, and helpers for Domain-Driven
              Design with support for entities, value objects, repositories, and
              domain events.
            </p>
          </Card>

          <Card className="p-6 bg-slate-900/50 border-slate-800">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 mb-4">
              <span className="text-2xl font-bold">2</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">CLI Tool</h3>
            <p className="text-sm text-slate-400">
              Command-line interface for scaffolding domains, entities,
              handlers, and complete CRUD operations with a single command.
            </p>
          </Card>
        </div>
      </section>

      {/* Requirements */}
      <section id="requirements" className="mb-12">
        <h2 className="text-3xl font-bold text-white mb-4">Requirements</h2>
        <p className="text-slate-300 mb-4">
          Before you begin, ensure you have the following installed:
        </p>
        <ul className="list-disc list-inside text-slate-300 space-y-2">
          <li>
            .NET 8 SDK or higher (we recommend using the latest LTS version)
          </li>
          <li>A code editor (Visual Studio 2022, VS Code, or Rider)</li>
          <li>Git (optional, but recommended)</li>
        </ul>
      </section>

      {/* Installation */}
      <section id="installation" className="mb-12">
        <h2 className="text-3xl font-bold text-white mb-4">
          <Download className="inline h-8 w-8 mr-2 text-emerald-400" />
          Installation
        </h2>

        <h3 className="text-2xl font-semibold text-white mb-4 mt-8">
          Step 1: Install the C# Library
        </h3>
        <p className="text-slate-300 mb-4">
          Add the AxisNet NuGet package to your project:
        </p>
        <div className="not-prose mb-6">
          <CodeBlock code="dotnet add package AxisNet.Framework" />
        </div>

        <h3 className="text-2xl font-semibold text-white mb-4 mt-8">
          Step 2: Install the CLI Tool
        </h3>
        <p className="text-slate-300 mb-4">
          Install AxisNet.Cli as a global tool:
        </p>
        <div className="not-prose mb-6">
          <CodeBlock code="dotnet tool install --global AxisNet.Cli" />
        </div>

        <p className="text-slate-300 mb-4">Verify the installation:</p>
        <div className="not-prose mb-6">
          <CodeBlock code="axisnet --version" />
        </div>

        <div className="not-prose">
          <Card className="p-4 bg-emerald-900/20 border-emerald-500/30 mb-6">
            <p className="text-sm text-emerald-300">
              <strong>Note:</strong> If the command is not recognized, make sure
              your .NET tools path is in your system's PATH environment
              variable.
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default GettingStarted;
